import React, { useRef } from "react";
import { motion } from "framer-motion";
import { Camera, Trophy, Medal, Award, Crown, Star, ChevronRight, Flame } from "lucide-react";
import { Avatar } from "./Avatar";

const rankStyle = (i) => {
  if (i === 0) return { ring: "ring-2 ring-[#ccff00]", icon: <Crown className="w-4 h-4 text-[#090d16]" />, bg: "bg-[#ccff00] text-[#090d16]" };
  if (i === 1) return { ring: "ring-1 ring-slate-400/50", icon: <Medal className="w-4 h-4 text-slate-200" />, bg: "bg-slate-500 text-white" };
  if (i === 2) return { ring: "ring-1 ring-[#ff9f1c]/50", icon: <Award className="w-4 h-4 text-white" />, bg: "bg-[#ff9f1c] text-white" };
  return { ring: "", icon: null, bg: "bg-[#1e2c4a] text-slate-300" };
};

const MONTHS = ["gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno", "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre"];
const monthLabel = (ym) => {
  if (!ym) return "";
  const [, m] = ym.split("-").map(Number);
  return MONTHS[m - 1] || "";
};

export const Leaderboard = ({ rows, mvp, pastMvps = [], currentName, onUploadPhoto, onOpenHistory }) => {
  if (!rows.length) {
    return (
      <div className="flex flex-col items-center justify-center text-center py-20" data-testid="leaderboard-empty">
        <div className="w-16 h-16 rounded-2xl bg-[#121b2d] border border-[#1e2c4a] flex items-center justify-center mb-4">
          <Trophy className="w-8 h-8 text-slate-500" />
        </div>
        <h3 className="font-heading font-bold text-xl text-slate-200">Classifica vuota</h3>
        <p className="text-slate-500 text-sm mt-1 max-w-xs">
          Registra il risultato di una partita completa per iniziare a guadagnare punti.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {mvp && <MvpBanner mvp={mvp} onOpenHistory={onOpenHistory} />}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {rows.map((p, i) => (
          <PlayerCard key={p.key} player={p} rank={i} currentName={currentName} onUploadPhoto={onUploadPhoto} onOpenHistory={onOpenHistory} isMvp={mvp && mvp.key === p.key} />
        ))}
      </div>
      {pastMvps.length > 0 && <HallOfFame pastMvps={pastMvps} onOpenHistory={onOpenHistory} />}
    </div>
  );
};

const HallOfFame = ({ pastMvps, onOpenHistory }) => (
  <div className="mt-8" data-testid="hall-of-fame">
    <div className="flex items-center gap-2 mb-3">
      <Trophy className="w-5 h-5 text-[#ff9f1c]" />
      <h3 className="font-heading font-extrabold text-xl text-white">Albo d'Oro</h3>
      <span className="text-xs text-slate-500">MVP dei mesi passati</span>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {pastMvps.map((p) => (
        <button
          key={p.month}
          onClick={() => onOpenHistory({ key: p.key, name: p.name, points: p.points, wins: p.wins, win_rate: p.win_rate, played: 0, losses: 0, photo_path: p.photo_path })}
          data-testid={`past-mvp-${p.month}`}
          className="text-left rounded-2xl bg-[#121b2d] border border-[#ff9f1c]/25 p-3 flex items-center gap-3 hover:border-[#ff9f1c]/50 transition-colors"
        >
          <div className="relative shrink-0">
            <Avatar name={p.name} photoPath={p.photo_path} size={44} className="ring-1 ring-[#ff9f1c]/50" />
            <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-[#ff9f1c] flex items-center justify-center border-2 border-[#121b2d]">
              <Crown className="w-2.5 h-2.5 text-[#090d16]" />
            </span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[11px] uppercase tracking-wider text-[#ff9f1c] font-bold">{monthLabel(p.month)} {p.month.split("-")[0]}</p>
            <p className="font-heading font-bold text-base text-slate-100 truncate">{p.name}</p>
            <p className="text-xs text-slate-400">{p.points} pt · {p.wins}V · {p.win_rate}%</p>
          </div>
        </button>
      ))}
    </div>
  </div>
);

const MvpBanner = ({ mvp, onOpenHistory }) => (
  <motion.button
    initial={{ opacity: 0, scale: 0.97 }}
    animate={{ opacity: 1, scale: 1 }}
    onClick={() => onOpenHistory(mvp)}
    data-testid="mvp-banner"
    className="w-full text-left rounded-2xl p-4 bg-gradient-to-br from-[#ccff00]/15 to-[#00e5ff]/5 border border-[#ccff00]/40 flex items-center gap-4 pulse-glow"
  >
    <div className="relative shrink-0">
      <Avatar name={mvp.name} photoPath={mvp.photo_path} size={56} className="ring-2 ring-[#ccff00]" />
      <span className="absolute -top-1.5 -right-1.5 w-7 h-7 rounded-full bg-[#ccff00] flex items-center justify-center border-2 border-[#121b2d]">
        <Star className="w-3.5 h-3.5 text-[#090d16] fill-[#090d16]" />
      </span>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-1.5 text-[#ccff00] text-xs font-bold uppercase tracking-wider">
        <Crown className="w-3.5 h-3.5" /> MVP di {monthLabel(mvp.month || "")}
      </div>
      <p className="font-heading font-extrabold text-xl text-white truncate mt-0.5">{mvp.name}</p>
      <p className="text-xs text-slate-300 mt-0.5">{mvp.points} punti · {mvp.wins} vittorie · {mvp.win_rate}%</p>
    </div>
    <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
  </motion.button>
);

const PlayerCard = ({ player, rank, currentName, onUploadPhoto, onOpenHistory, isMvp }) => {
  const fileRef = useRef(null);
  const rs = rankStyle(rank);
  const isMe = player.key === (currentName || "").toLowerCase();

  const pick = (e) => {
    const f = e.target.files?.[0];
    if (f) onUploadPhoto(player.name, f);
    e.target.value = "";
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, delay: rank * 0.04 }}
      data-testid={`player-card-${player.key}`}
      onClick={() => onOpenHistory(player)}
      className={`rounded-2xl bg-[#121b2d] border border-[#1e2c4a] p-4 flex items-center gap-4 cursor-pointer hover:border-[#ccff00]/40 transition-colors ${isMe ? "ring-1 ring-[#ccff00]/40" : ""}`}
    >
      <div className={`w-8 h-8 rounded-full flex items-center justify-center font-heading font-extrabold text-sm shrink-0 ${rs.bg}`}>
        {rs.icon || rank + 1}
      </div>

      <div className="relative shrink-0">
        <Avatar name={player.name} photoPath={player.photo_path} size={54} className={rs.ring} />
        {isMvp && (
          <span className="absolute -top-1 -left-1 w-5 h-5 rounded-full bg-[#ccff00] flex items-center justify-center border-2 border-[#121b2d]">
            <Star className="w-2.5 h-2.5 text-[#090d16] fill-[#090d16]" />
          </span>
        )}
        {isMe && (
        <button
          onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
          data-testid={`upload-photo-${player.key}`}
          className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-[#ccff00] text-[#090d16] flex items-center justify-center border-2 border-[#121b2d] hover:bg-[#aeea00]"
        >
          <Camera className="w-3 h-3" />
        </button>
        )}
        {isMe && <input ref={fileRef} type="file" accept="image/*" onClick={(e) => e.stopPropagation()} onChange={pick} className="hidden" data-testid={`photo-input-${player.key}`} />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="font-heading font-bold text-base text-slate-100 truncate">{player.name}</p>
          {isMe && <span className="text-[10px] font-bold text-[#ccff00] bg-[#ccff00]/10 rounded px-1.5 py-0.5">TU</span>}
          {player.streak >= 2 && (
            <span data-testid={`streak-${player.key}`} className="flex items-center gap-0.5 text-[10px] font-bold text-[#ff6b35] bg-[#ff6b35]/10 rounded px-1.5 py-0.5">
              <Flame className="w-3 h-3 fill-[#ff6b35]" />{player.streak}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
          <span><b className="text-slate-200">{player.played}</b> gioc.</span>
          <span className="text-[#ccff00]"><b>{player.wins}</b> V</span>
          <span className="text-[#ff4d4d]"><b>{player.losses}</b> S</span>
          <span className="text-[#00e5ff]" data-testid={`elo-${player.key}`}><b>ELO {player.elo ?? 1000}</b></span>
        </div>
      </div>

      <div className="text-right shrink-0">
        <p className="font-heading font-extrabold text-2xl text-[#ccff00] leading-none">{player.points}</p>
        <p className="text-[10px] uppercase tracking-wider text-slate-500">punti</p>
      </div>
    </motion.div>
  );
};
